/**
 * Движок синхронизации: первичное наполнение, replay, live-режим,
 * polling-fallback и полная пересинхронизация.
 *
 * CC §4.3 — первый запуск и вход; §4.5 — WebSocket; §4.8 — reset;
 * §3.3 — состояния синхронизации.
 */
import { api, unwrap } from '@shared/api/client'
import { ApiError } from '@shared/api/errors'
import type { Match, Me, SyncEvent } from '@shared/api/types'
import {
  blocksRepo,
  contactsRepo,
  conversationsRepo,
  matchesRepo,
  meRepo,
  messagesRepo,
  notificationsRepo,
  openUserDb,
  syncStateRepo,
} from '@shared/db'
import { dismissPairNotifications } from '@shared/push/dismiss'
import {
  SyncSocket,
  type ByeReason,
  type PresenceFrame,
  type SocketStatus,
  type TypingFrame,
} from '@shared/ws/socket'

import { applyEvent, type ApplyEffects } from './apply'
import {
  runQueue,
  setQueueContext,
  setReplayComplete,
  startQueueRunner,
  stopQueueRunner,
} from './queue-runner'

/** Итог обработки медиа на сервере. */
export interface MediaEvent {
  mediaId: string
  state: 'ready' | 'rejected'
  rejectedReason?: string
}

/** CC §3.3 — состояния синхронизации. */
export type SyncStatus =
  | 'idle'
  | 'initial_snapshot'
  | 'replaying'
  | 'live'
  | 'polling_fallback'
  | 'reset_required'
  | 'offline'

/** Интервал опроса, когда WebSocket недоступен (CC §4.5 п. 7). */
const POLLING_INTERVAL_MS = 30_000

export interface EngineCallbacks {
  onStatusChange?: (status: SyncStatus) => void
  onEffects?: (effects: ApplyEffects) => void
  onTyping?: (frame: TypingFrame) => void
  onPresence?: (frame: PresenceFrame) => void
  onSessionLost?: () => void
  onConsentRequired?: () => void
  onForbidden?: () => void
  onBye?: (reason: ByeReason) => void
  onNewMatch?: (match: Match) => void
}

export class SyncEngine {
  private socket: SyncSocket | null = null
  private status: SyncStatus = 'idle'
  private userId: string | null = null
  private pollingTimer: ReturnType<typeof setInterval> | null = null
  private activeConversationId: string | null = null
  private visibleConversations: string[] = []
  private wsFailures = 0

  /*
   * Эфемерные кадры раздаются подписчикам напрямую: typing и presence
   * не durable (BS §2.3), в реплику не пишутся и живут только пока открыт
   * соответствующий экран.
   */
  private readonly typingListeners = new Set<(frame: TypingFrame) => void>()
  private readonly presenceListeners = new Set<(frame: PresenceFrame) => void>()

  /**
   * Результат обработки медиа. В статусе `active` готовность приходит
   * событием `media.ready` / `media.rejected` и опрос НЕ требуется (CC §8.6),
   * поэтому загрузчику нужен способ дождаться именно события.
   */
  private readonly mediaListeners = new Set<(result: MediaEvent) => void>()

  /**
   * Недавние результаты обработки медиа.
   *
   * Событие `media.ready` может прийти РАНЬШЕ, чем загрузчик успеет
   * подписаться (обработка бывает быстрее круга промисов). Без этой памяти
   * подписчик ждал бы событие, которого уже не будет.
   */
  private readonly recentMedia = new Map<string, { result: MediaEvent; at: number }>()

  constructor(private readonly callbacks: EngineCallbacks = {}) {}

  /** Подписка экрана чата на индикатор набора собеседника. */
  onTypingFrame(listener: (frame: TypingFrame) => void): () => void {
    this.typingListeners.add(listener)
    return () => this.typingListeners.delete(listener)
  }

  /** Подписка на presence контрагентов видимых диалогов. */
  onPresenceFrame(listener: (frame: PresenceFrame) => void): () => void {
    this.presenceListeners.add(listener)
    return () => this.presenceListeners.delete(listener)
  }

  /** Подписка на готовность или отклонение медиа (CC §8.6). */
  onMediaEvent(listener: (result: MediaEvent) => void): () => void {
    this.mediaListeners.add(listener)
    return () => this.mediaListeners.delete(listener)
  }

  /** Результат, если он уже приходил, — закрывает гонку подписки. */
  takeMediaResult(mediaId: string): MediaEvent | null {
    const entry = this.recentMedia.get(mediaId)
    if (!entry) return null

    this.recentMedia.delete(mediaId)
    return entry.result
  }

  getStatus(): SyncStatus {
    return this.status
  }

  private setStatus(status: SyncStatus): void {
    if (this.status === status) return
    this.status = status
    this.callbacks.onStatusChange?.(status)
  }

  /* ===================================================================== */
  /* Запуск                                                                */
  /* ===================================================================== */

  /**
   * CC §4.3. Первый запуск = snapshot. При обычном запуске шаги 4–5
   * пропускаются: cursor берётся из `sync_state`.
   */
  async start(userId: string, initialMe?: Me): Promise<void> {
    this.userId = userId
    openUserDb(userId)
    setQueueContext(userId)

    const state = await syncStateRepo.init(userId)

    // Свежий `GET /me` со стартовой последовательности: `user.device_id` текущей
    // сессии нужен кадру hello и при обычном запуске (без snapshot) взять его больше неоткуда.
    if (initialMe) await meRepo.put(initialMe)

    if (state.cursor === null) {
      await this.fullResync()
    } else {
      this.setStatus('replaying')
    }

    this.connectSocket()
    startQueueRunner()
  }

  stop(): void {
    this.socket?.stop()
    this.socket = null
    this.stopPolling()
    stopQueueRunner()
    setQueueContext(null)
    setReplayComplete(false)
    this.userId = null
    this.setStatus('idle')
  }

  /* ===================================================================== */
  /* Полная пересинхронизация (CC §4.8)                                     */
  /* ===================================================================== */

  async fullResync(): Promise<void> {
    const userId = this.userId
    if (!userId) return

    this.setStatus('initial_snapshot')
    setReplayComplete(false)

    // Шаг 1: snapshot. Cursor НЕ сохраняется до шага 6.
    const snapshot = unwrap(await api.GET('/sync/snapshot'))
    // `user.device_id` необязателен в схеме Me: если snapshot его не вернул,
    // сохраняется значение текущей сессии из реплики (кадр hello без него невалиден).
    const previous = await meRepo.get(userId)
    const deviceId = snapshot.me.user.device_id ?? previous?.user.device_id
    await meRepo.put(
      deviceId ? { ...snapshot.me, user: { ...snapshot.me.user, device_id: deviceId } } : snapshot.me,
    )
    await blocksRepo.replaceAll(snapshot.blocked_user_ids)

    // Шаг 2: перезагрузка списков обычными endpoint'ами.
    await this.reloadLists()

    // Шаг 3: только теперь сохраняем cursor.
    await syncStateRepo.patch(userId, {
      cursor: snapshot.cursor,
      snapshot_at: new Date().toISOString(),
      status: 'live',
      applied_ids: [],
    })

    this.setStatus('replaying')
  }

  /**
   * CC §4.3 шаг 5: списки диалогов загружаются ЦЕЛИКОМ (все страницы),
   * это требование клиентского поиска C-02 (решение D-30, GAP-ALL-01).
   */
  private async reloadLists(): Promise<void> {
    const [active, archived] = await Promise.all([
      api.GET('/conversations', { params: { query: { filter: 'active' } as never } }),
      api.GET('/conversations', { params: { query: { filter: 'archived' } as never } }),
    ])

    await conversationsRepo.putMany([...unwrap(active).items, ...unwrap(archived).items])

    const [matches, notifications, contacts] = await Promise.all([
      api.GET('/matches', { params: { query: { filter: 'all' } as never } }),
      api.GET('/notifications', { params: { query: { limit: 50 } as never } }),
      // Изменение v1.6: контакты — такой же список реплики, как совпадения
      api.GET('/contacts', { params: { query: { limit: 100 } as never } }),
    ])

    await matchesRepo.putMany(unwrap(matches).items)
    await notificationsRepo.putMany(unwrap(notifications).items)
    await contactsRepo.replaceAll(unwrap(contacts).items)
  }

  /* ===================================================================== */
  /* WebSocket                                                             */
  /* ===================================================================== */

  private connectSocket(): void {
    if (this.socket) return

    this.socket = new SyncSocket(
      {
        onEvent: (event) => this.handleEvent(event),

        onReady: async (frame) => {
          if (frame.reset_required) {
            // CC §4.5 п. 3: закрыть соединение и выполнить процедуру §4.8.
            this.setStatus('reset_required')
            this.socket?.suspend()
            await this.fullResync()
            this.socket?.resume()
            return
          }

          if (!frame.replay_complete) {
            this.setStatus('replaying')
            return
          }

          // §4.5 п. 5: после replay_complete — кадр visible и повтор очереди.
          this.wsFailures = 0
          this.stopPolling()
          this.setStatus('live')
          setReplayComplete(true)

          this.socket?.setVisibleConversations(this.visibleConversations)
          this.socket?.setActiveConversation(this.activeConversationId)
          void runQueue()
        },

        onTyping: (frame) => {
          this.callbacks.onTyping?.(frame)
          for (const listener of this.typingListeners) listener(frame)
        },

        onPresence: (frame) => {
          this.callbacks.onPresence?.(frame)
          for (const listener of this.presenceListeners) listener(frame)
        },

        onFrameError: (code) => {
          // Нефатально: `not_member` — перезагрузить диалог, соединение не рвём.
          if (code === 'not_member') void this.reloadLists()
        },

        onBye: (reason) => {
          this.callbacks.onBye?.(reason)
          if (reason === 'session_revoked') this.callbacks.onSessionLost?.()
          if (reason === 'consent_required') this.callbacks.onConsentRequired?.()
        },

        onSessionInvalid: () => {
          setReplayComplete(false)
          this.callbacks.onSessionLost?.()
        },

        onForbidden: () => {
          setReplayComplete(false)
          this.callbacks.onForbidden?.()
        },

        onStatusChange: (socketStatus) => this.handleSocketStatus(socketStatus),
      },
      () => this.buildHello(),
    )

    this.socket.start()
  }

  private async buildHello(): Promise<{
    cursor: string | null
    deviceId: string | null
    appVersion: string
    activeConversationId: string | null
    visibleConversations: string[]
  }> {
    const userId = this.userId
    const cursor = userId ? await syncStateRepo.getCursor(userId) : null
    const me = userId ? await meRepo.get(userId) : undefined

    return {
      cursor,
      deviceId: me?.user.device_id ?? null,
      appVersion: __APP_VERSION__,
      activeConversationId: this.activeConversationId,
      visibleConversations: this.visibleConversations,
    }
  }

  private handleSocketStatus(socketStatus: SocketStatus): void {
    if (socketStatus === 'reconnecting') {
      setReplayComplete(false)
      this.wsFailures += 1

      // После нескольких неудач переходим в режим опроса (CC §4.5 п. 7).
      if (this.wsFailures >= 3) this.startPolling()
    }
  }

  /* ===================================================================== */
  /* Polling-fallback                                                       */
  /* ===================================================================== */

  private startPolling(): void {
    if (this.pollingTimer) return
    this.setStatus('polling_fallback')
    setReplayComplete(true)

    const poll = (): void => {
      if (document.visibilityState !== 'visible') return
      void this.pollOnce()
    }

    poll()
    this.pollingTimer = setInterval(poll, POLLING_INTERVAL_MS)
  }

  private stopPolling(): void {
    if (!this.pollingTimer) return
    clearInterval(this.pollingTimer)
    this.pollingTimer = null
  }

  private async pollOnce(): Promise<void> {
    const userId = this.userId
    if (!userId) return

    try {
      const cursor = await syncStateRepo.getCursor(userId)
      const result = unwrap(await api.GET('/sync', { params: { query: { cursor, limit: 500 } as never } }))

      for (const event of result.events) await this.handleEvent(event)

      if (result.has_more) await this.pollOnce()
      else void runQueue()
    } catch (error) {
      if (error instanceof ApiError && error.code === 'sync_reset_required') {
        await this.fullResync()
        return
      }
      // Сетевой отказ — следующий тик попробует снова.
    }
  }

  /* ===================================================================== */
  /* Применение события                                                     */
  /* ===================================================================== */

  private async handleEvent(event: SyncEvent): Promise<void> {
    const userId = this.userId
    if (!userId) return

    let effects: ApplyEffects
    try {
      effects = await applyEvent(event, { userId })
    } catch {
      // Ошибка применения не должна ронять поток: cursor не сдвинут,
      // событие придёт снова при следующем replay.
      return
    }

    if (effects.sessionRevoked) this.callbacks.onSessionLost?.()
    if (effects.consentRequired) this.callbacks.onConsentRequired?.()
    if (effects.matchCreated) this.callbacks.onNewMatch?.(effects.matchCreated)

    // §8.5 п. 5: показанные уведомления пары снимаются при block/unmatch.
    if (effects.dismissNotifications?.length) {
      for (const target of effects.dismissNotifications) {
        void dismissPairNotifications(target)
      }
    }

    // §9.3 п. 7: бинарный кэш service worker инвалидируется по событиям.
    if (effects.invalidateMediaUrls?.length) {
      navigator.serviceWorker?.controller?.postMessage({
        type: 'MEDIA_INVALIDATE',
        urls: effects.invalidateMediaUrls,
      })
    }

    // Готовое медиа разблокирует сообщение в состоянии `pending_media`.
    if (effects.mediaReady) {
      await this.releasePendingMedia(effects.mediaReady)
      this.emitMedia({ mediaId: effects.mediaReady, state: 'ready' })
    }

    if (effects.mediaRejected) {
      await this.failPendingMedia(effects.mediaRejected)
      this.emitMedia({
        mediaId: effects.mediaRejected,
        state: 'rejected',
        rejectedReason: (event.data as { rejected_reason?: string }).rejected_reason ?? 'other',
      })
    }

    this.callbacks.onEffects?.(effects)
  }

  private emitMedia(result: MediaEvent): void {
    this.recentMedia.set(result.mediaId, { result, at: Date.now() })
    this.pruneRecentMedia()

    for (const listener of this.mediaListeners) {
      try {
        listener(result)
      } catch {
        /* подписчик не должен ломать поток событий */
      }
    }
  }

  /** Память результатов живёт минуту — дольше она никому не нужна. */
  private pruneRecentMedia(): void {
    const cutoff = Date.now() - 60_000
    for (const [mediaId, entry] of this.recentMedia) {
      if (entry.at < cutoff) this.recentMedia.delete(mediaId)
    }
  }

  private async releasePendingMedia(mediaId: string): Promise<void> {
    for (const row of await messagesRepo.waitingForMedia(mediaId)) {
      await messagesRepo.setLocalState(row.conversation_id, row.seq, { local_state: 'queued' })
    }
    void runQueue()
  }

  private async failPendingMedia(mediaId: string): Promise<void> {
    for (const row of await messagesRepo.waitingForMedia(mediaId)) {
      // ПС §20.9: сообщение не должно выглядеть отправленным.
      await messagesRepo.setLocalState(row.conversation_id, row.seq, {
        local_state: 'failed',
        error_code: 'media_rejected',
      })
    }
  }

  /* ===================================================================== */
  /* Подсказки соединению                                                   */
  /* ===================================================================== */

  setActiveConversation(conversationId: string | null): void {
    this.activeConversationId = conversationId
    this.socket?.setActiveConversation(conversationId)
  }

  setVisibleConversations(ids: readonly string[]): void {
    this.visibleConversations = [...ids]
    this.socket?.setVisibleConversations(ids)
  }

  sendTyping(conversationId: string, state: 'start' | 'stop'): void {
    this.socket?.sendTyping(conversationId, state)
  }

  /** Квитанции: по WS, если соединение живо, иначе HTTP через очередь. */
  sendReceipt(conversationId: string, cursors: { delivered_seq?: number; read_seq?: number }): boolean {
    return this.socket?.sendReceipt(conversationId, cursors) ?? false
  }

  /** Возврат вкладки в foreground — немедленная попытка (CC §4.5 п. 7). */
  reconnectNow(): void {
    this.socket?.reconnectNow()
  }

  /**
   * Браузер сообщил, что сеть вернулась (событие `online`, CC §3.2).
   *
   * `navigator.onLine` — только подсказка; подтверждением служит обмен с
   * сервером. Если соединение живо (heartbeat идёт), оно и есть подтверждение,
   * и состояние нужно объявить заново: `reconnectNow()` в этом случае ничего
   * не делает, и приложение навсегда осталось бы в состоянии «нет сети».
   */
  confirmOnline(): void {
    if (this.socket?.isConnected()) {
      this.callbacks.onStatusChange?.(this.status)
      return
    }

    this.reconnectNow()
  }

  /**
   * SYS-05: фоновые запросы и reconnect приостанавливаются
   * до успешного `POST /me/consents` (CC §4.5, BS §15.1).
   */
  suspendForConsent(): void {
    this.socket?.suspend()
  }

  resumeAfterConsent(): void {
    this.socket?.resume()
  }
}
